/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation} from 'react-query';
import {
  createScheduledReport,
  deleteScheduledReport,
  editScheduledReport
} from '@ion/core/src/services/Reports';
import {GenericObject, ScheduledReport} from '@ion/core/src/types/entities';
import {createMutationResult} from '../utils/helpers';

const mutationMap = {
  create : createScheduledReport,
  edit   : editScheduledReport,
  delete : deleteScheduledReport
};

/**
 * Custom hook for handling create, edit, or delete mutations for scheduled Reports.
 * This hook uses React Query's `useMutation` to execute the corresponding mutation 
 * based on the mode provided ('create', 'edit', 'delete'). The hook allows providing
 * custom success and error callbacks via `onSuccess` and `onError`.
 *
 * ```typescript
 * const {save, mutation} = useScheduledReportMutation('create', handleSuccess, handleError);
 * 
 * save({name: 'New scheduled report'}); // Executes the create mutation with the provided payload.
 * ```
 * 
 * @param mode - The operation mode to determine which mutation to execute. Possible values are:
 *               - 'create': To create a new scheduledReport.
 *               - 'edit': To edit an existing scheduledReport.
 *               - 'delete': To delete an existing scheduledReport.
 *               - Defaults to 'create'.
 *
 * @param onSuccess - Optional callback function to be called on mutation success.
 * @param onError   - Optional callback function to be called on mutation error.
 *
 * @returns An object with:
 *          - `mutation`: The mutation object from React Query, providing status and mutation methods.
 *          - `save`: A callback function to trigger the mutation with a payload (of type `GenericObject`).
 *             It invokes the appropriate mutation based on the `mode` parameter.
 */
export const useScheduledReportMutation = (
  mode: 'create' | 'edit' | 'delete' = 'create',
  onSuccess?: Function,
  onError?: Function
) => {
  const mutation = useMutation(
    mutationMap[mode], {
      onSuccess : onSuccess as any,
      onError   : onError as any
    }
  );

  const save = useCallback((payload: ScheduledReport) => {
    mutation.mutate(payload);
  }, [mutation]);

  return createMutationResult(
    mutation, {save}
  ) as any;
};

export default useScheduledReportMutation;
